import { action } from 'mobx';
import getTwilioVideo from 'components/LiveClass/utils/getTwilioVideo';
import { SnackBarHelper } from '@lyearn-team/frontend-components';
import UserMediaStore from './UserMediaStore';

class LocalUserMediaStore extends UserMediaStore {
  initUserMedia(participant, twilioRoom) {
    super.initUserMedia(participant, twilioRoom);

    this.isVideoEnabled = this.videoPublication ? this.videoPublication.track.isStarted : false;
    this.isAudioEnabled = this.audioPublication ? this.audioPublication.track.isStarted : false;
    this.isScreenShareEnabled = this.screenSharePublication
      ? this.screenSharePublication.track.isStarted
      : false;

    this.participant
      .off('trackStopped', this.handleTrackStopped)
      .on('trackStopped', this.handleTrackStopped)
      .off('trackPublished', this.handleTrackPublished)
      .on('trackPublished', this.handleTrackPublished);
  }

  @action
  handleTrackPublished = (publication) => {
    switch (publication.trackName) {
      case 'video':
        this.isVideoEnabled = true;
        this.videoPublication = publication;
        break;
      case 'audio':
        this.isAudioEnabled = true;
        this.audioPublication = publication;
        break;
      case 'screen':
        this.isScreenShareEnabled = true;
        this.screenPublication = publication;
        break;
      default:
        break;
    }
  };

  @action
  handleTrackStopped = (track) => {
    switch (track.name) {
      case 'video':
        this.isVideoEnabled = false;
        this.videoPublication.unpublish();
        break;
      case 'audio':
        this.isAudioEnabled = false;
        this.audioPublication.unpublish();
        break;
      case 'screen':
        this.isScreenShareEnabled = false;
        this.screenSharePublication.unpublish();
        break;
      default:
        break;
    }
  };

  @action
  toggleVideo = async () => {
    if (this.isVideoEnabled) {
      this.videoPublication.track.stop();
    } else {
      try {
        const { createLocalVideoTrack } = await getTwilioVideo();
        const localTrack = await createLocalVideoTrack({ name: 'video' });
        const twilioRoom = this.store.room.twilioRoom;
        await twilioRoom.localParticipant.publishTrack(localTrack, { name: 'video' });
      } catch (error) {
        console.log('Error during enable video', error);
        window.bugsnagClient && window.bugsnagClient.notify(error);
        SnackBarHelper.showSnackbar({
          label:
            'Error enabling video, please check webcam is connected. If problem still persists, try restarting your browser or contact support.',
          status: 'error'
        });
      }
    }
  };

  @action
  toggleAudio = async () => {
    if (this.isAudioEnabled) {
      this.audioPublication.track.stop();
    } else {
      try {
        const { createLocalAudioTrack } = await getTwilioVideo();
        const localTrack = await createLocalAudioTrack({ name: 'audio' });
        const twilioRoom = this.store.room.twilioRoom;
        await twilioRoom.localParticipant.publishTrack(localTrack, { name: 'audio' });
      } catch (error) {
        console.log('Error during enable audio', error);
        window.bugsnagClient && window.bugsnagClient.notify(error);
        SnackBarHelper.showSnackbar({
          label:
            'Error enabling audio, please check microphone is connected. If problem still persists, try restarting your browser or contact support.',
          status: 'error'
        });
      }
    }
  };

  @action
  toggleScreenShare = async () => {
    if (this.isScreenShareEnabled) {
      this.screenSharePublication.track.stop();
    } else {
      try {
        const { LocalVideoTrack } = await getTwilioVideo();
        const stream = await navigator.mediaDevices.getDisplayMedia();
        const localTrack = new LocalVideoTrack(stream.getTracks()[0], { name: 'screen' });
        const twilioRoom = this.store.room.twilioRoom;
        await twilioRoom.localParticipant.publishTrack(localTrack, { name: 'screen' });
      } catch (error) {
        console.log('Error during enable screenshare', error);
        window.bugsnagClient && window.bugsnagClient.notify(error);
        SnackBarHelper.showSnackbar({ label: 'Error enabling screenshare.', status: 'error' });
      }
    }
  };
}

export default LocalUserMediaStore;
